import { Link } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import { logoutAndRedirect } from '@/lib/auth/logout'
import { startClubCtaHandler } from '@/lib/analytics/startClubCta'
import { WORKSPACE_CTA, type PublicCta } from './publicNav'
import { RowInner, SkeletonRow } from './AppNavLinks'
import { APP_NAV_ITEMS, COMPANY_ITEMS, HELP_ITEM, PROFILE_ITEM, manageItemsFor } from './navItems'
import { NavGroup } from './navRows'
import styles from './AppNavLinks.module.css'

/** Mode-specific signed-out CTA cluster (Sign in target + one primary button). */
export type SignedOutNav = { signInTo: string; primary: PublicCta }

type Props = {
  /** Called after any row activates, so the host drawer/sheet closes itself. */
  onNavigate?: () => void
  /** Signed-out CTA set. Defaults to the marketing variant; PublicMobileSheet
   *  passes the player-mode variant so the sheet keeps its per-mode CTA. */
  signedOut?: SignedOutNav
  /** Drop the signed-in MANAGE (Workspace / Admin) + ACCOUNT (Profile / Sign
   *  out) groups. Set ONLY where the avatar dropdown (PublicAccountMenu) already
   *  carries those links on the same surface — the public header, where the
   *  avatar shows at every width — so they aren't duplicated menu-vs-avatar. Left
   *  false (default) for the in-app AppDrawer, whose top-bar avatar is only a
   *  /profile jump, making the drawer the sole account reach on phone/tablet. */
  hideAccountGroups?: boolean
  /** Surface the COMPANY group (Product / Pricing / Solutions / Guides / About
   *  us) below 1024px, where the horizontal header hides its own copy of it.
   *
   *  OPT-IN, AND OFF BY DEFAULT (owner, 2026-09-07). It used to render for every
   *  viewer on every surface, which put the marketing menu on a phone in exactly
   *  the places the owner had just taken it off a desktop — the player pages and
   *  inside the workspace, where Guides was still one tap away. The ruling is
   *  about the surface, so the group follows the same line the bar does: only
   *  PublicMobileSheet in MARKETING mode asks for it. */
  company?: boolean
  /** NAVIGATION AND NOTHING ELSE — the app list, no COMPANY group, no MANAGE /
   *  ACCOUNT tails, no CTA. Set only by the CLUB STOREFRONT (owner's standing
   *  rule: a club's own page must not shout the platform at its visitors, so the
   *  Product / Pricing / Solutions / Guides / About group cannot appear there),
   *  where the header's avatar dropdown already carries the account links at
   *  every width — the same reason PublicMobileSheet passes hideAccountGroups. */
  navOnly?: boolean
}

const DEFAULT_SIGNED_OUT: SignedOutNav = {
  signInTo: '/login',
  primary: { label: 'Start your club', to: '/workspace', ctaSource: 'drawer' },
}

/**
 * The single, shared hamburger-drawer CONTENT — the LEFT HAMBURGER DRAWER (owner
 * IA 2026-07-22): app navigation. Rendered inside BOTH the public sheet
 * (PublicMobileSheet, reachable on desktop + mobile now) and the in-app drawer
 * (AppDrawer) so the two surfaces render one polished, icon'd, active-aware menu
 * that cannot drift.
 *
 * Content:
 *   - APP-NAV always leads, and there is nothing to choose: every surface draws
 *     APP_NAV_ITEMS, the one list in navItems (owner, 2026-09-07 — no Home
 *     anywhere, the board row under its own name). This used to take an `items`
 *     prop so the workspace could pass a second, shorter list; the prop is gone
 *     with the second list rather than left defaulted, so no host can quietly
 *     hand this component a menu of its own.
 *   - Signed-in also carries MANAGE (Workspace / Admin, gated) and an ACCOUNT
 *     tail (Profile / Sign out) so the in-app mobile drawer — where the top bar's
 *     avatar is only a /profile jump — keeps full reach; the "Go to workspace"
 *     CTA closes it out for managers. On the PUBLIC header the avatar dropdown
 *     (PublicAccountMenu) already carries Manage + Account at every width, so the
 *     public sheet passes `hideAccountGroups` to drop those two groups and read
 *     as pure app-navigation (owner IA 2026-07-24: no menu-vs-avatar dupes). The
 *     "Go to workspace" CTA is a header action and stays.
 *   - Signed-out adds Sign in + the per-mode primary CTA (the desktop header's
 *     Sign-in link is hidden on mobile, so the drawer carries it).
 *   - COMPANY (Product / Pricing / Solutions / Guides / About us) is drawn only
 *     where `company` asks for it — the MARKETING sheet, and nowhere else since
 *     2026-09-07 — and even there only at mobile / tablet width, exactly where
 *     the horizontal header hides its marketing nav (<1024px). At desktop width
 *     the group drops out (pure CSS, `styles.companyMobileOnly`) because those
 *     links already live in the header, so they are never duplicated there.
 * Rows + groups come from navRows over AppNavLinks.module.css — the same
 * primitives the avatar dropdown uses — so every surface shares one source.
 */
export function DrawerNavContent({
  onNavigate,
  signedOut = DEFAULT_SIGNED_OUT,
  hideAccountGroups = false,
  navOnly = false,
  company = false,
}: Props) {
  const { state, isSuperAdmin, isAnyClubStaff } = useAuth()
  const isAuthed = state === 'authenticated'
  const isChecking = state === 'checking'
  const superAdmin = isAuthed && isSuperAdmin()
  const canManage = isAuthed && isAnyClubStaff()

  // Marketing links, mobile/tablet only (hidden ≥1024px, where the horizontal
  // header carries them). Declared once and dropped into both signed states so
  // the two branches cannot end up disagreeing about who sees it.
  const companyGroup = company ? (
    <div className={styles.companyMobileOnly}>
      <NavGroup label="Company" leadDivider items={COMPANY_ITEMS} onNavigate={onNavigate} />
    </div>
  ) : null

  // THE STOREFRONT'S DRAWER — the app list on its own, in ALL THREE states. The groups
  // below are dropped by rule, not by circumstance: COMPANY is the platform's marketing
  // menu and must never appear on a club's page, and MANAGE / ACCOUNT / the CTA already
  // live in the storefront header's avatar dropdown a few pixels away. What is left is
  // the one thing the storefront had none of: a way to navigate.
  //
  // IT ANSWERS BEFORE `checking` DOES, and that is the point of the order. The skeleton
  // below exists to avoid flashing a WRONG SIGNED STATE — and this branch has no signed
  // state to get wrong: signed in, signed out and still-resolving all render the same
  // five rows. Reserving a footprint for a shape that is already known would only make
  // the menu change shape once auth lands.
  if (navOnly) {
    return (
      <nav className={styles.nav} aria-label="Main">
        <NavGroup label="Explore" items={APP_NAV_ITEMS} onNavigate={onNavigate} />
      </nav>
    )
  }

  if (isChecking) {
    // Reserve a stable footprint while POST /auth/refresh resolves, so the menu
    // settles into its real shape instead of flashing a wrong signed state.
    return (
      <nav className={styles.nav} aria-label="Main" aria-busy="true">
        <ul className={styles.list}>
          <li className={styles.item}>
            <SkeletonRow width="72px" />
          </li>
          <li className={styles.item}>
            <SkeletonRow width="60px" />
          </li>
          <li className={styles.item}>
            <SkeletonRow width="66px" />
          </li>
        </ul>
      </nav>
    )
  }

  if (isAuthed) {
    return (
      <nav className={styles.nav} aria-label="Main">
        {/* APP-NAV — the drawer's core, for every signed-in viewer. */}
        <NavGroup label="Explore" items={APP_NAV_ITEMS} onNavigate={onNavigate} />

        {companyGroup}

        {/* MANAGE + ACCOUNT — hidden where the avatar dropdown (PublicAccountMenu)
            already carries them (public header, avatar at every width), so they
            aren't duplicated menu-vs-avatar. Kept where the drawer is the only
            account reach (in-app phone/tablet: the top-bar avatar is a /profile
            jump only). MANAGE stays club-manager-only; Admin super-admin-only. */}
        {!hideAccountGroups && canManage ? (
          <NavGroup
            label="Manage"
            leadDivider
            items={manageItemsFor({ clubStaff: canManage, superAdmin })}
            onNavigate={onNavigate}
          />
        ) : null}

        {/* ACCOUNT tail — Profile, Help + the Sign out button. */}
        {!hideAccountGroups ? (
          <NavGroup label="Account" leadDivider items={[PROFILE_ITEM, HELP_ITEM]} onNavigate={onNavigate}>
            <li className={styles.item}>
              <button
                type="button"
                className={styles.link}
                onClick={() => {
                  onNavigate?.()
                  void logoutAndRedirect()
                }}
              >
                <RowInner icon="logout" label="Sign out" />
              </button>
            </li>
          </NavGroup>
        ) : null}

        {canManage ? (
          <Link to={WORKSPACE_CTA.to} onClick={onNavigate} className={styles.primary}>
            {WORKSPACE_CTA.label}
          </Link>
        ) : null}
      </nav>
    )
  }

  // Signed-out — the same app-nav, then Sign in + the per-mode primary CTA.
  return (
    <nav className={styles.nav} aria-label="Main">
      <NavGroup label="Explore" items={APP_NAV_ITEMS} onNavigate={onNavigate} />

      {companyGroup}

      <hr className={styles.divider} />
      <ul className={styles.list} aria-label="More">
        <li className={styles.item}>
          <Link to={signedOut.signInTo} onClick={onNavigate} className={styles.link}>
            <RowInner icon="profile" label="Sign in" />
          </Link>
        </li>
      </ul>

      <Link
        to={signedOut.primary.to}
        onClick={startClubCtaHandler(signedOut.primary.ctaSource, onNavigate)}
        className={styles.primary}
      >
        {signedOut.primary.label}
      </Link>
    </nav>
  )
}
